import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Globe, Facebook, Instagram, Shield, Database, Share2, Lock, Cookie, Mail, AlertTriangle } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');

  const content = language === 'en' ? {
    title: 'Privacy Policy',
    subtitle: 'How we collect, use, and protect your information',
    updated: 'Last updated: December 2025',
    intro: "Athena's Online Shopping Services (\"we\", \"us\", \"our\") respects your privacy. This policy explains what information we collect when you use our website and forms, and how we use and protect it.",
    sections: [
      {
        icon: Database,
        title: 'Information We Collect',
        items: [
          'Contact details you provide: full name, email address, phone number, and delivery address in Belize.',
          'Order details: product links, descriptions, and quantities you submit in a quote request.',
          'Partner application details: your location and business experience when you apply to become a partner.'
        ]
      },
      {
        icon: Shield,
        title: 'How We Use Your Information',
        items: [
          'To prepare and send you a price quote for the products you request.',
          'To contact you about your quote, order, or partner application.',
          'To arrange purchasing, shipping, and delivery of your items to Belize.',
          'To respond to your questions and provide customer support.'
        ]
      },
      {
        icon: Share2,
        title: 'Sharing of Information',
        items: [
          'We do not sell your personal information.',
          'Quote and application form submissions are delivered to our business email through EmailJS, a third-party email delivery service.',
          'We may share necessary details (such as delivery address) with shipping carriers to fulfill your order.'
        ]
      },
      {
        icon: Cookie,
        title: 'Cookies & Tracking',
        items: [
          'Our website may use basic cookies and standard analytics to keep the site working and understand general usage.',
          'We do not use your information for third-party advertising.'
        ]
      },
      {
        icon: Lock,
        title: 'Data Security & Retention',
        items: [
          'We take reasonable steps to protect your information and keep it only as long as needed to serve you.',
          'You may request that we update or delete your information by contacting us.'
        ]
      }
    ],
    shippingTitle: 'Shipping to Our Warehouse — Liability Disclaimer',
    shippingBody: "If you (or a vendor on your behalf) purchase items from a USA store and have them delivered to our USA warehouse so that we can ship them to Belize, we are NOT responsible for any loss, theft, or damage that occurs while the vendor or carrier is delivering those items to our warehouse. Our responsibility begins only once your items have been received and checked in at our warehouse. We strongly recommend purchasing shipping insurance from your chosen USA store or carrier.",
    contactTitle: 'Contact Us',
    contactDesc: 'If you have any questions about this Privacy Policy, please reach out:',
    backToHome: 'Back to Home'
  } : {
    title: 'Política de Privacidad',
    subtitle: 'Cómo recopilamos, usamos y protegemos su información',
    updated: 'Última actualización: Diciembre 2025',
    intro: 'Athena\'s Online Shopping Services ("nosotros") respeta su privacidad. Esta política explica qué información recopilamos cuando usa nuestro sitio web y formularios, y cómo la usamos y protegemos.',
    sections: [
      {
        icon: Database,
        title: 'Información que Recopilamos',
        items: [
          'Datos de contacto que usted proporciona: nombre completo, correo electrónico, número de teléfono y dirección de entrega en Belice.',
          'Detalles del pedido: enlaces de productos, descripciones y cantidades que envía en una solicitud de cotización.',
          'Detalles de solicitud de socio: su ubicación y experiencia comercial cuando solicita ser socio.'
        ]
      },
      {
        icon: Shield,
        title: 'Cómo Usamos Su Información',
        items: [
          'Para preparar y enviarle una cotización de los productos que solicita.',
          'Para contactarlo sobre su cotización, pedido o solicitud de socio.',
          'Para organizar la compra, el envío y la entrega de sus artículos a Belice.',
          'Para responder a sus preguntas y brindar atención al cliente.'
        ]
      },
      {
        icon: Share2,
        title: 'Compartir Información',
        items: [
          'No vendemos su información personal.',
          'Los envíos de formularios de cotización y solicitud se entregan a nuestro correo comercial a través de EmailJS, un servicio de entrega de correo de terceros.',
          'Podemos compartir los datos necesarios (como la dirección de entrega) con transportistas para completar su pedido.'
        ]
      },
      {
        icon: Cookie,
        title: 'Cookies y Seguimiento',
        items: [
          'Nuestro sitio web puede usar cookies básicas y análisis estándar para mantener el sitio funcionando y comprender el uso general.',
          'No usamos su información para publicidad de terceros.'
        ]
      },
      {
        icon: Lock,
        title: 'Seguridad y Retención de Datos',
        items: [
          'Tomamos medidas razonables para proteger su información y la conservamos solo el tiempo necesario para atenderlo.',
          'Puede solicitar que actualicemos o eliminemos su información contactándonos.'
        ]
      }
    ],
    shippingTitle: 'Envío a Nuestro Almacén — Aviso de Responsabilidad',
    shippingBody: 'Si usted (o un proveedor en su nombre) compra artículos en una tienda de EE.UU. y los envía a nuestro almacén en EE.UU. para que los reenviemos a Belice, NO somos responsables de ninguna pérdida, robo o daño que ocurra mientras el proveedor o transportista entrega esos artículos a nuestro almacén. Nuestra responsabilidad comienza solo una vez que sus artículos han sido recibidos y verificados en nuestro almacén. Recomendamos encarecidamente comprar un seguro de envío de la tienda o transportista de EE.UU. que elija.',
    contactTitle: 'Contáctenos',
    contactDesc: 'Si tiene preguntas sobre esta Política de Privacidad, comuníquese:',
    backToHome: 'Volver al Inicio'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f5f2] to-white" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#8b1538] hover:text-[#6b0f2a]"
              data-testid="privacy-back-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{content.backToHome}</span>
            </Button>

            <img
              src="https://customer-assets.emergentagent.com/job_57e2dd96-4e50-451b-8abf-042890e7a51b/artifacts/m648krxt_Athena%27s%20Logo.png"
              alt="Athena's"
              className="h-12 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="flex items-center gap-2"
              data-testid="privacy-lang-toggle"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ES' : 'EN'}
            </Button>
            <a
              href="https://www.facebook.com/profile.php?id=61584846392424"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a
              href="https://www.instagram.com/athenasonlineshoppingservices/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-10">
            <Shield className="w-16 h-16 text-[#8b1538] mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-4">{content.title}</h1>
            <p className="text-lg text-[#736c64]">{content.subtitle}</p>
            <p className="text-sm text-[#736c64] mt-2">{content.updated}</p>
          </div>

          <Card className="mb-6 border-2 border-[#8b1538]/20">
            <CardContent className="p-6">
              <p className="text-[#1e1919] leading-relaxed">{content.intro}</p>
            </CardContent>
          </Card>

          {content.sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Card key={index} className="mb-6 border-2 border-[#3dd3ee]/30">
                <CardHeader className="bg-gradient-to-r from-[#3dd3ee]/10 to-transparent">
                  <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                    <Icon className="w-6 h-6 text-[#8b1538]" />
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#736c64]">
                        <div className="w-2 h-2 bg-[#8b1538] rounded-full mt-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}

          <Card className="mb-6 border-2 border-[#ff8c19]/40 bg-[#ff8c19]/5" data-testid="privacy-shipping-liability">
            <CardHeader className="bg-gradient-to-r from-[#ff8c19]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <AlertTriangle className="w-6 h-6 text-[#ff8c19]" />
                {content.shippingTitle}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-[#736c64] leading-relaxed">{content.shippingBody}</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#fad24b]/40 bg-[#fad24b]/5">
            <CardContent className="p-6 text-center">
              <Mail className="w-10 h-10 text-[#8b1538] mx-auto mb-3" />
              <h3 className="text-xl font-bold text-[#1e1919] mb-2">{content.contactTitle}</h3>
              <p className="text-[#736c64] mb-4">{content.contactDesc}</p>
              <div className="space-y-1 text-[#1e1919]">
                <p>
                  Phone: <a href="tel:+5016735717" className="text-[#8b1538] font-semibold hover:underline">6735717</a>
                </p>
                <p>
                  Email: <a href="mailto:athenasonlineshoppingservices@gmail.com" className="text-[#8b1538] font-semibold hover:underline break-all">athenasonlineshoppingservices@gmail.com</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <a
              href="https://www.facebook.com/profile.php?id=61584846392424"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a
              href="https://www.instagram.com/athenasonlineshoppingservices/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
          <p className="text-[#bbb5ae] text-sm mb-3">
            Business Hours: 10 AM - 9 PM (7 Days)
          </p>
          <div className="mb-4 text-sm space-y-1">
            <p className="text-[#bbb5ae]">
              Phone: <a href="tel:+5016735717" data-testid="footer-phone-link" className="text-[#fad24b] hover:text-white transition-colors">6735717</a>
            </p>
            <p className="text-[#bbb5ae]">
              WhatsApp: <a href="https://wa.me/5016735717" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="text-[#fad24b] hover:text-white transition-colors">Chat with us</a>
            </p>
            <p className="text-[#bbb5ae]">
              Email: <a href="mailto:athenasonlineshoppingservices@gmail.com" data-testid="footer-email-link" className="text-[#fad24b] hover:text-white transition-colors break-all">athenasonlineshoppingservices@gmail.com</a>
            </p>
            <p className="text-[#bbb5ae]">
              <a href="/privacy" data-testid="footer-privacy-link" className="text-[#fad24b] hover:text-white transition-colors">Privacy Policy</a>
            </p>
          </div>
          <p className="text-[#bbb5ae] text-sm">
            © {new Date().getFullYear()} Athena's Online Shopping Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
